<template>
  <div
    class="pivot-charts-wraper"
    ref="tableWraperRef"
  >
    <div ref="searchRef">
      <Search
        v-if="
          config?.search_config &&
          config.search_config.length > 0 &&
          !enable_advanced_search_multigroups
        "
        :searchConfig="config?.search_config || []"
        :autoRefreshOnSearchChange="config?.auto_refresh_on_search_change"
        v-model:searchConditions="searchConditions"
        :event-bus="selfEventBus"
        :searchType="search_type"
        :searchDisplayType="config?.search_display_type"
        :searchColumnCount="config?.search_column_count"
        :viewSearchConfig="viewSearchConfig"
        :disabledShowSearchGroupName="config?.disabledShowSearchGroupName"
        @viewSearchConfigChange="viewSearchConfigChange"
        @change="handleSearchChange"
      ></Search>
      <SearchFormGroup
        v-if="
          config?.search_config &&
          config.search_config.length > 0 &&
          enable_advanced_search_multigroups
        "
        :searchConfig="config?.search_config || []"
        :autoRefreshOnSearchChange="config?.auto_refresh_on_search_change"
        v-model:searchGroups="searchGroups"
        :event-bus="selfEventBus"
        :searchType="search_type"
        :searchDisplayType="config?.search_display_type"
        :searchColumnCount="config?.search_column_count"
        :disabledShowSearchGroupName="config?.disabledShowSearchGroupName"
        @change="handleSearchChange"
      ></SearchFormGroup>
    </div>
    <a-card
      ref="elRef"
      :body-style="{ padding: 0 }"
    >
      <TableHeader
        :config="config"
        :auth_batch_update_list="auth_batch_update_list"
        :auth_head_btn_list="auth_head_btn_list"
        :dataSource="dataSource"
        :current_org="current_org"
        :router="router"
        :selfEventBus="selfEventBus"
        :loading="loading"
        :screenState="screenState"
        v-model:settingOpen="settingOpen"
        :columns="columns"
        :view_key="view_key"
        :view_flag_name="config.title"
        :is_view="is_view"
        :refreshType="refreshType"
        :pivotChartsConfig="pivotChartsConfig"
        :component_type="'pivot_charts'"
        @refresh="refresh"
        @setFull="setFull"
        @exitFull="exitFull"
        @pivotChartsConfigChange="onPivotChartsConfigChange"
        @heightChange="h => (headerHeight = h)"
        @refreshTypeChange="handleRefreshTypeChange"
      >
        <template #header_bar_before_title>
          <slot name="header_bar_before_title"></slot>
        </template>
        <template #header_bar_after_title>
          <slot name="header_bar_after_title"></slot>
        </template>
        <template #header_bar_before_btn>
          <slot name="header_bar_before_btn"></slot>
        </template>
        <template #header_bar_after_btn>
          <slot name="header_bar_after_btn"></slot>
        </template>
      </TableHeader>
      <div
        class="pivot-echarts-container"
        ref="echartsContainerRef"
        :style="{ height: tableHeight + 'px' }"
      >
        <div
          v-if="loading && !config?.disabled_table_loading"
          class="pivot-echarts-loading"
        >
          <a-spin />
        </div>
        <div
          v-if="hasPivotChartsConfig"
          ref="echartsRef"
          class="echarts-dom"
          :style="{ width: '100%', height: '100%' }"
        ></div>
        <div
          v-else
          class="pivot-empty-tip"
        >
          点击右上角"设置"按钮配置透视图字段
        </div>
      </div>
    </a-card>
  </div>
</template>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import { getApiData } from 'liyu-pc-base/api/api-model';
import { filterConditions, genID } from 'liyu-pc-base/utils/function';
import { useFullscreen } from 'liyu-pc-base/utils/hooks/useFullscreen';
import { debounce, isArray } from 'lodash';
import mitt from 'mitt';
import { computed, onMounted, onUnmounted, provide, reactive, ref, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import * as echarts from 'echarts';
import Search from './search.vue';
import SearchFormGroup from './SearchFormGroup.vue';
import TableHeader from './tableHeader.vue';
import { useRequiredConditionStore, required_condition_event_obj } from 'liyu-pc-base/init-plugin';
import { useI18n } from 'vue-i18n';
import { useEnv, useUserStore } from 'liyu-pc-base/init-plugin';
import {
  Column,
  SearchConditionItem,
  SearchGroupItem,
  Table,
  TableProState,
  ViewData,
  ViewSearchItem,
  PivotChartsConfig,
  RefreshType,
} from './typing';
import { fetchViewsData, getDefaultSearchOperator, getParamsCount, tableProStateSymbol } from './utils';
import { useView } from './hooks/view';
import { query_view_settings_list } from 'liyu-pc-base/api/view-model';
import { ConditionOperator } from 'liyu-pc-base/api/typing.js';
import { formatPivotValue } from './func';

const props = defineProps({
  config: {
    type: Object as () => Table,
    required: true,
    default: () => ({
      id: '',
      title: '',
      is_normal_model: true,
      modelType: null,
      columns: [],
    }),
  },
  eventBus: {
    type: Object,
    required: false,
  },
});

interface Emits {
  (e: 'dataChange', params: { data: any[] }): void;
}
const emit = defineEmits<Emits>();
const { t } = useI18n();
const env = useEnv();
const requiredConditionStore = useRequiredConditionStore();
const userStore = useUserStore();
const userid = userStore.userid;
const current_org = userStore.currentOrg;
const VIEW_SYSTEM_EDIT = 'view_system_edit';
const is_view_system_edit_auth = ref(userStore.CHECK_AUTH(VIEW_SYSTEM_EDIT));
const router = useRouter();
const dataSource = ref<any[]>([]);
const loading = ref(false);
const selfEventBus = mitt();
const searchConditions = ref<SearchConditionItem[]>([]);
const searchGroups = ref<SearchGroupItem[]>([]);
const enable_advanced_search_multigroups = computed(
  () => !!props.config.enable_advanced_search_multigroups,
);

let loadDataId = null;
const tableWraperRef = ref(null);
const tableWraperHeight = ref(null);
const searchRef = ref(null);
const searchHeight = ref(null);
const headerHeight = ref(0);
const echartsContainerRef = ref(null);
const echartsRef = ref<HTMLElement | null>(null);
let echartsInstance: echarts.ECharts | null = null;
const auth_batch_update_list = ref([]);
const auth_head_btn_list = ref<any[]>([]);
const auth_btn_list = ref<any[]>([]);
const refreshType = ref(props.config.refreshType);
const settingOpen = ref(false);

// 透视图配置
const pivotChartsConfig = ref<PivotChartsConfig>({
  series: [],
  category: [],
  values: [],
  chartType: 'bar',
});

// 后端返回的图表数据
const chartData = ref<any>(null);

const getAuthBtn = () => {
  auth_btn_list.value = userStore.CHECK_AUTH_BY_BTN_LIST(props.config?.btn_list || []) || [];
  auth_head_btn_list.value =
    userStore.CHECK_AUTH_BY_BTN_LIST(props.config?.head_btn_list || []) || [];
  auth_batch_update_list.value =
    userStore.CHECK_AUTH_BY_BTN_LIST(props.config?.batch_update_list || []) || [];
};
getAuthBtn();

const view_key = computed(() => {
  return `table_pro_${props.config.key}`;
});

useResizeObserver(tableWraperRef, entries => {
  tableWraperHeight.value = entries[0].contentRect.height;
});
useResizeObserver(searchRef, entries => {
  searchHeight.value = entries[0].contentRect.height;
});
useResizeObserver(echartsContainerRef, () => {
  if (echartsInstance) {
    echartsInstance.resize();
  }
});

const search_type = computed(() => {
  if (props.config.search_type === 'simple') {
    return props.config.search_type;
  } else {
    return 'advanced';
  }
});

const is_view = computed(() => {
  return props.config?.is_view !== false;
});

const viewSearchConfig = ref<Record<string, ViewSearchItem>>({});

const getQueryParams = ({ type = 'list', viewData = null } = {}) => {
  const getColumnFields = (list: Column[]) => {
    const fields: string[] = [];
    list.forEach(item => {
      if (
        item.dataIndex &&
        item.dataIndex !== 'index' &&
        item.dataIndex !== 'action' &&
        item.checked !== false
      ) {
        fields.push(item.dataIndex);
      }
      if (item.children && item.children.length > 0) {
        fields.push(...getColumnFields(item.children));
      }
    });
    return fields;
  };

  const params: any = {
    ...(props.config.query_params || {}),
  };

  if (type === 'list') {
    const column_fields = getColumnFields(columns.value || []);
    if (props.config.base_column_fields) {
      column_fields.push(...props.config.base_column_fields);
    }
    params['is_pagination'] = false;
  }

  if (search_type.value === 'advanced') {
    if (enable_advanced_search_multigroups.value) {
      const searchGroupConds = (viewData ? viewData.searchGroups || [] : searchGroups.value)
        .map(group => filterConditions(group.conditions))
        .filter(group => group.length > 0);
      const baseConds = filterConditions(props.config.base_conditions);
      const conditionsList: any[] = [];
      if (searchGroupConds.length > 0) {
        conditionsList.push({ op: 'and', children: searchGroupConds });
      }
      if (baseConds.length > 0) {
        conditionsList.push({ op: 'and', children: baseConds });
      }
      params['conditions'] = conditionsList;
    } else {
      const conditions = [
        ...filterConditions(props.config.base_conditions),
        ...filterConditions(viewData ? viewData.searchConditions || [] : searchConditions.value),
      ];
      params['conditions'] = [conditions];
    }
  } else if (search_type.value === 'simple') {
    for (const item of filterConditions(
      viewData ? viewData.searchConditions || [] : searchConditions.value,
    )) {
      const v_count = getParamsCount(item.operator, item);
      let value = item.value;
      if (v_count === 1) {
        value = value[0];
      }
      params[item.field_name] = value;
    }
  }

  // 透视图配置
  // 注意：这里整体透传配置对象（与 pivotTable 的 pivot_table_config 保持一致），
  // 而非白名单式逐字段挑选 —— 否则 subExpressions / aggComputedValues 等
  // 后续新增的配置项会被静默丢弃，导致后端拿不到参数。
  // 仅 chartType 需要额外补一个后端约定的 snake_case 键 chart_type。
  if (type === 'list' && hasPivotChartsConfig.value) {
    params['pivot_charts_config'] = {
      ...pivotChartsConfig.value,
      chart_type: pivotChartsConfig.value.chartType,
    };
  }

  if (props.config.get_query_params_func) {
    const newParams = props.config.get_query_params_func({ params, type });
    if (newParams) {
      Object.assign(params, newParams);
    }
  }

  const requiredConditionObj = getRequiredConditionObj();
  if (requiredConditionObj) {
    params['required_condition'] = requiredConditionObj;
  }
  if (props.config.get_db_alias) {
    params['db_alias'] = props.config.get_db_alias();
  }

  return params;
};

const [elRef, screenState, { setFull, exitFull }] = useFullscreen();

const getData = async () => {
  // 透视图模式下，未配置字段时不请求任何接口
  if (!hasPivotChartsConfig.value) {
    return;
  }
  if (!props.config.pivot_charts_url) {
    return;
  }
  const currentLoadId = Symbol();
  loadDataId = currentLoadId;
  try {
    loading.value = true;
    const params = getQueryParams({ type: 'list' });

    const useUrl = props.config.pivot_charts_url;

    let response: any;
    if (props.config.query_data_func) {
      response = await props.config.query_data_func(params);
    } else {
      response = await getApiData(params, useUrl.replace('__tree', ''), useUrl.includes('__tree'), {
        api_log: `pivotCharts ${props.config.key} ${props.config.title}`,
      });
    }
    if (loadDataId !== currentLoadId) {
      return;
    }
    if (props.config.process_data_func) {
      dataSource.value = props.config.process_data_func(response || []);
    }
    chartData.value = response;
    renderChart();
    emit('dataChange', {
      data: dataSource.value,
    });
  } finally {
    if (loadDataId !== currentLoadId) {
      return;
    }
    loading.value = false;
  }
};

const delayGetDate = debounce(getData, 500);

// ============ ECharts 渲染 ============

const hasPivotChartsConfig = computed(() => {
  return (
    pivotChartsConfig.value.series.length > 0 ||
    pivotChartsConfig.value.category.length > 0 ||
    pivotChartsConfig.value.values.length > 0
  );
});

/**
 * 初始化 ECharts 实例
 */
const initECharts = () => {
  if (!echartsRef.value) return;
  if (echartsInstance) {
    echartsInstance.dispose();
  }
  echartsInstance = echarts.init(echartsRef.value);
};

/**
 * 构建 ECharts option
 */
const buildEChartsOption = (): echarts.EChartsOption => {
  if (!chartData.value) return {};

  // 后端只回传值字段 id（series.id / categoryIds），formatFun 由前端据 id 从本地配置查回，
  // 纯前端做格式化（后端不再下发 formatFun）。
  const { categories, series, chart_type, categoryIds } = chartData.value;
  const configChartType = pivotChartsConfig.value.chartType || 'bar';
  const showPercent = !!pivotChartsConfig.value.showPercent;
  // 饼图与圆环图共用同一套渲染逻辑，仅半径不同
  const isPieType = configChartType === 'pie' || configChartType === 'donut';

  // 值字段 id → 显示格式（formatFun + formatPattern）映射（来自当前透视图配置的 values 与聚合计算值）
  type ValueFormat = { formatFun?: string; formatPattern?: string };
  const formatById: Record<string, ValueFormat> = {};
  (pivotChartsConfig.value.values || []).forEach(v => {
    if (v.id) formatById[v.id] = { formatFun: v.formatFun, formatPattern: v.formatPattern };
  });
  // 聚合计算值：后端回传的 series id 即其 id，按 id 查回显示格式
  (pivotChartsConfig.value.aggComputedValues || []).forEach(a => {
    if (a.id) formatById[a.id] = { formatFun: a.formatFun, formatPattern: a.formatPattern };
  });
  const getFormatFunById = (id?: string): ValueFormat | undefined => {
    if (!id) return undefined;
    return formatById[id];
  };

  // 格式化数值（duration_* → 时间间隔；numfmt → 按 formatPattern 用 numfmt 库格式化；
  // 旧配置 key（'0.00' 等）自动视为 numfmt 格式串）
  const formatValue = (value: number, fmt?: ValueFormat): string => {
    if (value === null || value === undefined) return '';
    if (typeof value !== 'number') return String(value);
    if (!fmt?.formatFun) return String(value);
    return formatPivotValue(value, fmt.formatFun, fmt.formatPattern);
  };

  // 获取某个 series 的显示格式（后端在 series 上附带值字段 id，前端据 id 查回）
  const getSeriesFormatFun = (seriesIndex: number): ValueFormat | undefined => {
    return getFormatFunById(series?.[seriesIndex]?.id);
  };

  // 获取某个 data point 的显示格式（用于 categoryIds 场景：只有系列/无类别无系列，
  // 此时类别即值标题，逐类别对应一个值字段 id）
  const getCategoryFormatFun = (dataIndex: number): ValueFormat | undefined => {
    return getFormatFunById(categoryIds?.[dataIndex]);
  };

  // 构建 tooltip 格式化函数
  const tooltipFormatter = (params: any) => {
    if (isArray(params)) {
      let html = params[0]?.name || '';
      params.forEach(item => {
        // 优先用 series 对应值字段的 formatFun，其次用 categoryIds
        const formatFun =
          getSeriesFormatFun(item.seriesIndex) || getCategoryFormatFun(item.dataIndex);
        html += `<br/>${item.marker}${item.seriesName}: ${formatValue(item.value, formatFun)}`;
      });
      return html;
    } else {
      // 饼图等 item trigger 场景
      const formatFun =
        getCategoryFormatFun(params.dataIndex) || getSeriesFormatFun(params.seriesIndex);
      // 第一行显示该环所属的系列（图例）名，便于区分嵌套圆环各自代表的系列
      let str = `<b>${params.seriesName}</b><br/>${params.name}: ${formatValue(
        params.value,
        formatFun,
      )}`;
      if (showPercent) {
        str += ` (${params.percent}%)`;
      }
      return str;
    }
  };

  // 饼图/圆环图特殊处理
  if (isPieType) {
    const pieSeries: any[] = [];
    if (series && series.length > 0) {
      const ringCount = series.length;
      const isNested = ringCount > 1;
      // 嵌套同心圆环：多个图例（系列）合并到同一张图，每个系列渲染为一圈
      const maxR = 75; // 最外圈外半径(%)
      const centerHole = isNested ? 8 : 0; // 嵌套时中心留小孔
      const band = isNested ? (maxR - centerHole) / ringCount : 0;

      series.forEach((s: any, sIndex: number) => {
        const pieData = categories.map((cat: string, cIndex: number) => ({
          name: cat,
          value: s.data[cIndex],
        }));
        let radius: any;
        let labelPosition: any;
        if (!isNested) {
          // 单个系列：圆环图为环形，饼图为整圆
          radius = configChartType === 'donut' ? ['40%', '70%'] : '60%';
          labelPosition = 'outside';
        } else {
          // sIndex=0 为最外圈，向内依次嵌套
          const outer = maxR - sIndex * band;
          const inner = maxR - (sIndex + 1) * band;
          radius = [`${inner}%`, `${outer}%`];
          // 外圈标签向外，内圈标签向内，避免相互遮挡
          labelPosition = sIndex === 0 ? 'outside' : 'inside';
        }
        pieSeries.push({
          name: s.name,
          type: 'pie',
          radius,
          center: ['50%', '50%'],
          data: pieData,
          label: {
            show: true,
            position: labelPosition,
            formatter: (params: any) => {
              const formatFun =
                getCategoryFormatFun(params.dataIndex) || getSeriesFormatFun(sIndex);
              let str = `${params.name}: ${formatValue(params.value, formatFun)}`;
              if (showPercent) {
                str += ` (${params.percent}%)`;
              }
              return str;
            },
          },
        });
      });
    }
    return {
      tooltip: {
        trigger: 'item',
        formatter: tooltipFormatter,
      },
      legend: {
        type: 'scroll',
        bottom: 0,
      },
      series: pieSeries,
    };
  }

  // 柱状图/折线图/面积图
  const chartType = configChartType === 'area' ? 'line' : configChartType;
  const echartSeries = (series || []).map((s: any) => {
    return {
      name: s.name,
      type: chartType,
      data: s.data,
      areaStyle: configChartType === 'area' ? {} : undefined,
      label: {
        show: true,
        position: 'top',
        formatter: (params: any) => {
          // 优先用 series 对应值字段 id 查回的 formatFun，其次用 categoryIds
          const formatFun = getFormatFunById(s.id) || getCategoryFormatFun(params.dataIndex);
          return formatValue(params.value, formatFun);
        },
      },
    };
  });

  // yAxis formatFun: 用第一个 series 的 formatFun，或 categoryIds 的第一个
  const yAxisFormatFun = getSeriesFormatFun(0) || getCategoryFormatFun(0);

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: tooltipFormatter,
    },
    legend: {
      type: 'scroll',
      bottom: 0,
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '8%',
      top: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: categories || [],
      axisLabel: {
        rotate: (categories || []).length > 10 ? 30 : 0,
        interval: 0,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value: number) => {
          return formatValue(value, yAxisFormatFun);
        },
      },
    },
    series: echartSeries,
    dataZoom:
      (categories || []).length > 20
        ? [
            { type: 'inside', start: 0, end: 100 },
            { type: 'slider', start: 0, end: 100 },
          ]
        : undefined,
  };
};

/**
 * 渲染图表
 */
const renderChart = async () => {
  await nextTick();
  if (!hasPivotChartsConfig.value) return;
  if (!echartsInstance) {
    initECharts();
  }
  if (echartsInstance) {
    const option = buildEChartsOption();
    echartsInstance.setOption(option, true);
  }
};

// ============ 表头高度计算 ============

const tableHeight = computed(() => {
  if (!tableWraperHeight.value) {
    return 400;
  }
  let value = tableWraperHeight.value - searchHeight.value - headerHeight.value - 4;
  if (value < 200) {
    value = 200;
  }
  return value;
});

// ============ 列配置 ============

const columns = computed<Column[]>(() => {
  const baseColumns = [...(props.config.columns || [])];
  const hasIndex = baseColumns.some(item => item.dataIndex === 'index');
  if (!hasIndex && !props.config.hide_index_column) {
    baseColumns.unshift({
      title: '#',
      dataIndex: 'index',
      key: 'index',
      maxWidth: 50,
      fixed: 'left',
      checked: true,
      autoHeight: true,
    });
  }
  return baseColumns;
});

// ============ 视图相关 ============

const viewColumnsConfig = ref<Record<string, any>>({});
const viewDataList = ref([]);
const currentView = ref<any>(null);
const isViewChange = ref(false);

const onPivotChartsConfigChange = (newConfig: PivotChartsConfig) => {
  pivotChartsConfig.value = newConfig;
  isViewChange.value = true;
  // 配置变更后重新获取数据
  if (hasPivotChartsConfig.value) {
    getData();
  } else {
    chartData.value = null;
    if (echartsInstance) {
      echartsInstance.clear();
    }
  }
};

const handleRefreshTypeChange = (type: string) => {
  refreshType.value = type as RefreshType;
  isViewChange.value = true;
};

const handleSearchChange = () => {
  isViewChange.value = true;
};

const viewSearchConfigChange = (config: Record<string, ViewSearchItem>) => {
  viewSearchConfig.value = config;
  isViewChange.value = true;
};

const refresh = async () => {
  await getData();
};

const local_refresh = async () => {
  if (echartsInstance) {
    renderChart();
  }
};

const getRequiredConditionObj = () => {
  const required_condition = props.config.required_condition;
  if (required_condition) {
    const required_condition_obj: Record<string, any> = {};
    required_condition.forEach((item: any) => {
      const value = (requiredConditionStore as any).value_map[item.key];
      if (value !== undefined && value !== null) {
        required_condition_obj[item.field_name] = value;
      }
    });
    return required_condition_obj;
  } else {
    return null;
  }
};

// ============ 初始化 ============

const init = async () => {
  const tmpSearchConditions: SearchConditionItem[] = [];
  if (props.config?.search_config) {
    props.config.search_config.forEach(configItem => {
      if (configItem.default_value) {
        let value = configItem.default_value;
        if (!isArray(value)) {
          value = [value];
        }
        tmpSearchConditions.push({
          field_name: configItem.field_name,
          value: value,
          operator: getDefaultSearchOperator(configItem) as ConditionOperator,
          format: configItem.format || 'input',
          class_name: configItem.class_name || 'str',
        });
      }
    });
  }

  searchConditions.value = tmpSearchConditions;
  if (enable_advanced_search_multigroups.value) {
    if (props.config.default_advanced_search_multigroups) {
      searchGroups.value = props.config.default_advanced_search_multigroups;
    } else {
      searchGroups.value = [
        {
          id: genID(),
          conditions: [...tmpSearchConditions],
          viewSearchConfig: {},
        },
      ];
    }
    searchConditions.value = [];
  }

  if (is_view.value) {
    if (currentView.value?.id !== 'no-view') {
      currentView.value = null;
      await fetchViews();
      if (props.config?.view_uid) {
        const findView = viewDataList.value.find((item: any) => item.uid == props.config?.view_uid);
        if (findView) {
          setViewData(findView.data);
          currentView.value = findView;
        } else {
          const res: any = await query_view_settings_list({
            org_id: current_org.id,
            is_active: true,
            uid: props.config?.view_uid,
          });
          if (res?.length) {
            currentView.value = { ...res[0], is_other_user_view: true };
          }
        }
      }
      if (!currentView.value && viewDataList.value?.length) {
        const tmpData = [...viewDataList.value];
        const getSortValue = (item: any) => {
          let value = item.sort || 0;
          if (item.id !== currentView.value?.id) {
            value *= 1000000;
          }
          if (!item.is_default) {
            value *= 10000;
          }
          if (!item.user_id) {
            value *= 1000;
          }
          return value;
        };
        tmpData.sort((a, b) => getSortValue(a) - getSortValue(b));
        setViewData(tmpData[0].data);
        currentView.value = tmpData[0];
      }
    }
  }
  await getData();
};

const eventReinit = async () => {
  isViewChange.value = false;
  await init();
};

const eventReload = async () => {
  await getData();
};

const getViewData = (): ViewData => {
  const viewData: any = {
    columns: viewColumnsConfig.value,
    searchConditions: searchConditions.value,
    refreshType: refreshType.value,
    searchConfig: viewSearchConfig.value,
    pivotChartsConfig: pivotChartsConfig.value,
  };
  if (enable_advanced_search_multigroups.value) {
    viewData.searchGroups = searchGroups.value;
    if (searchGroups.value.length > 0) {
      viewData.searchConfig = searchGroups.value[0].viewSearchConfig;
    }
  }
  if (props.config.get_view_data_func) {
    const newViewData = props.config.get_view_data_func({ viewData });
    if (newViewData) {
      Object.assign(viewData, newViewData);
    }
  }
  return viewData;
};

const setViewData = async (viewData: any) => {
  if (viewData === null) {
    await init();
    isViewChange.value = false;
    return;
  }
  if (viewData.columns) {
    viewColumnsConfig.value = viewData.columns;
  }
  if (enable_advanced_search_multigroups.value) {
    if (viewData.searchGroups && viewData.searchGroups.length > 0) {
      searchGroups.value = viewData.searchGroups;
    } else if (viewData.searchConditions) {
      searchGroups.value = [
        {
          id: genID(),
          conditions: viewData.searchConditions,
          viewSearchConfig: viewData.searchConfig || {},
        },
      ];
    }
    if (searchGroups.value.length === 0) {
      searchGroups.value = [
        {
          id: genID(),
          conditions: [],
          viewSearchConfig: {},
        },
      ];
    }
    searchConditions.value = [];
  } else {
    if (viewData.searchConfig) {
      viewSearchConfig.value = viewData.searchConfig;
    }
    if (viewData.searchConditions) {
      searchConditions.value = viewData.searchConditions;
    }
  }
  if (viewData.refreshType) {
    refreshType.value = viewData.refreshType;
  }
  // 加载透视图配置
  if (viewData.pivotChartsConfig) {
    pivotChartsConfig.value = viewData.pivotChartsConfig;
  }
  if (props.config.set_view_callback) {
    props.config.set_view_callback({ viewData });
  }
  await refresh();
  isViewChange.value = false;
};

const fetchViews = async () => {
  const data = await fetchViewsData(
    view_key.value,
    getQueryParams,
    props.config.count_url,
    props.config,
  );
  viewDataList.value = data;
};

const { saveView, addView } = useView({
  currentView,
  getViewData,
  isViewChange,
  userid,
  view_project__flag: env.VITE_APP_VIEW_PROJECT_FLAG,
  view_menu__flag: view_key.value,
  view_menu__name: props.config.title,
  fetchViews,
  is_auth: is_view_system_edit_auth,
  t,
});

provide<TableProState>(tableProStateSymbol, {
  setViewData,
  getViewData,
  fetchViews,
  viewDataList,
  currentView,
  isViewChange,
  config: props.config,
});

onMounted(async () => {
  if (!props.config.key) {
    console.error('pivotCharts config 未配置 key');
  }
  if (!props.config.title) {
    console.error('pivotCharts config 未配置 title');
  }
  required_condition_event_obj().on('change', handleRequiredConditionChange);
  selfEventBus.on('refresh', refresh);
  selfEventBus.on('local_refresh', local_refresh);
  selfEventBus.on('reinit', eventReinit);
  selfEventBus.on('saveView', saveView);
  selfEventBus.on('saveNewView', addView);
  if (props.eventBus) {
    props.eventBus.on('reinit', eventReinit);
    props.eventBus.on('refresh', refresh);
    props.eventBus.on('local_refresh', local_refresh);
    props.eventBus.on('reload_table', eventReload);
  }
  await init();
});

const handleRequiredConditionChange = (key: string) => {
  if (props.config.required_condition) {
    const item = props.config.required_condition.find((item: any) => item.key === key);
    if (item) {
      getData();
    }
  }
};

onUnmounted(() => {
  selfEventBus.off('refresh', refresh);
  selfEventBus.off('local_refresh', local_refresh);
  selfEventBus.off('reinit', eventReinit);
  selfEventBus.off('saveView', saveView);
  selfEventBus.off('saveNewView', addView);
  if (props.eventBus) {
    props.eventBus.off('reinit', eventReinit);
    props.eventBus.off('refresh', refresh);
    props.eventBus.off('local_refresh', local_refresh);
    props.eventBus.off('reload_table', eventReload);
  }
  if (echartsInstance) {
    echartsInstance.dispose();
    echartsInstance = null;
  }
});

watch(
  () => props.config.query_params,
  () => {
    delayGetDate();
  },
  { deep: true },
);

watch(
  () => props.config.base_conditions,
  () => {
    delayGetDate();
  },
  { deep: true },
);

watch(
  () => pivotChartsConfig.value.chartType,
  () => {
    if (chartData.value) {
      renderChart();
    }
  },
);

defineExpose({
  getViewData,
  setViewData,
  config: props.config,
  columns,
  fetchViews,
  viewDataList,
  currentView,
  isViewChange,
  pivotChartsConfig,
  refresh,
  init,
  dataSource,
  get echartsInstance() {
    return echartsInstance;
  },
  settingOpen,
});
</script>

<style lang="less" scoped>
.pivot-charts-wraper {
  height: 100%;
  overflow: hidden;
}
.pivot-echarts-container {
  position: relative;
  width: 100%;
  overflow: hidden;

  .pivot-echarts-loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    background: rgba(255, 255, 255, 0.6);
  }

  .echarts-dom {
    width: 100%;
    height: 100%;
  }

  .pivot-empty-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #999;
    font-size: 14px;
  }
}
</style>
